import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getMatchWithDetailsByBroadcastId, getTeams, getTournament, getClubLogoUrl, getBroadcastSettings, getPlayerOfMatch } from '@/lib/supabase';
import { SetRecord, Rally, Match, MATCH_CONFIG } from '@/types';
import { resolveServingInfo, ServingParticipant } from '@/lib/matchUtils';

const POLL_INTERVAL = 2000;
const BROADCAST_SETTINGS_POLL_INTERVAL = 2000;

// Inject transparent background styles for broadcast overlay
const overlayStyles = `
  /* Force transparent backgrounds at all levels - must override Tailwind */
  html, body, #root, #root > div {
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
    min-height: auto !important;
    height: auto !important;
  }
  /* Override Tailwind's bg-background utility */
  .bg-background {
    background: transparent !important;
    background-color: transparent !important;
  }
  /* Ensure no pseudo-elements create backgrounds */
  body::before, body::after, #root::before, #root::after, #root > div::before, #root > div::after {
    display: none !important;
    background: transparent !important;
  }
  /* Hide scrollbars */
  body, html {
    overflow: hidden !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
  }
  body::-webkit-scrollbar, html::-webkit-scrollbar {
    display: none !important;
  }
`;

type MatchData = Match & {
  sets: (SetRecord & { rallies: Rally[] })[];
  started_at?: string | null;
  ended_at?: string | null;
  best_catcher_ids?: string[] | null;
};

interface TeamInfo {
  id: string;
  name: string;
  shortName: string;
  color: string;
  logoUrl?: string | null;
  players?: { id: string; name: string }[];
}

export default function BroadcastOverlay() {
  const { broadcastId: urlBroadcastId } = useParams<{ broadcastId: string }>();
  const [resolvedBroadcastId, setResolvedBroadcastId] = useState<string | null>(null);
  const [match, setMatch] = useState<MatchData | null>(null);
  const [teamA, setTeamA] = useState<TeamInfo | null>(null);
  const [teamB, setTeamB] = useState<TeamInfo | null>(null);
  const [servingTeam, setServingTeam] = useState<ServingParticipant | null>(null);
  const [serverName, setServerName] = useState<string | null>(null);
  const [tournamentName, setTournamentName] = useState<string>('');
  const [clubLogoUrl, setClubLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRallyText, setLastRallyText] = useState<{ text: string; key: number } | null>(null);
  const prevScoreRef = useRef<{ teamA: number; teamB: number }>({ teamA: 0, teamB: 0 });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const broadcastPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Determine the broadcast ID to use
  // If URL is /overlay/live, we need to fetch from broadcast_settings
  // If URL is /overlay/TB001, we use the URL param directly
  const isLiveRoute = urlBroadcastId === 'live';

  // Poll broadcast_settings for the live route
  useEffect(() => {
    if (!isLiveRoute) {
      // For specific broadcast ID routes, use the URL param directly
      setResolvedBroadcastId(urlBroadcastId || null);
      return;
    }

    // For /overlay/live, poll the broadcast_settings table
    const fetchBroadcastSettings = async () => {
      try {
        const settings = await getBroadcastSettings();
        if (settings?.activeBroadcastId) {
          setResolvedBroadcastId(settings.activeBroadcastId);
        } else {
          setResolvedBroadcastId(null);
          setMatch(null);
          setLoading(false);
        }
      } catch (err) {
        console.error('[BroadcastOverlay] Error fetching broadcast settings:', err);
      }
    };

    fetchBroadcastSettings();
    broadcastPollRef.current = setInterval(fetchBroadcastSettings, BROADCAST_SETTINGS_POLL_INTERVAL);

    return () => {
      if (broadcastPollRef.current) clearInterval(broadcastPollRef.current);
    };
  }, [isLiveRoute, urlBroadcastId]);

  const fetchData = useCallback(async () => {
    if (!resolvedBroadcastId) return;

    try {
      const data = await getMatchWithDetailsByBroadcastId(resolvedBroadcastId);
      if (!data) return;

      const matchData = data as unknown as MatchData;
      setMatch(matchData);

      const teamAId = matchData.teamAId ?? (matchData as any).team_a_id;
      const teamBId = matchData.teamBId ?? (matchData as any).team_b_id;

      const teamsData = await getTeams();
      const teamAMap = teamsData.find((t: any) => t.id === teamAId) as any;
      const teamBMap = teamsData.find((t: any) => t.id === teamBId) as any;

      if (teamAMap) {
        setTeamA({
          id: teamAMap.id,
          name: teamAMap.name,
          shortName: teamAMap.short_name ?? teamAMap.shortName,
          color: teamAMap.color,
          logoUrl: teamAMap.logo_url ?? teamAMap.logoUrl,
          players: teamAMap.players ?? [],
        });
      }
      if (teamBMap) {
        setTeamB({
          id: teamBMap.id,
          name: teamBMap.name,
          shortName: teamBMap.short_name ?? teamBMap.shortName,
          color: teamBMap.color,
          logoUrl: teamBMap.logo_url ?? teamBMap.logoUrl,
          players: teamBMap.players ?? [],
        });
      }

      const teamAParticipant: ServingParticipant | null = teamAMap ? {
        id: teamAMap.id,
        shortName: teamAMap.short_name ?? teamAMap.shortName,
        color: teamAMap.color,
        players: teamAMap.players ?? [],
      } : null;
      const teamBParticipant: ServingParticipant | null = teamBMap ? {
        id: teamBMap.id,
        shortName: teamBMap.short_name ?? teamBMap.shortName,
        color: teamBMap.color,
        players: teamBMap.players ?? [],
      } : null;

      // Resolve serving info for live matches
      const { servingTeam: resolvedServingTeam, serverName: resolvedServerName } = resolveServingInfo(
        (matchData as any).current_serving_team_id,
        (matchData as any).current_server_id,
        teamAParticipant,
        teamBParticipant,
      );

      setServingTeam(resolvedServingTeam);
      setServerName(resolvedServerName);

      // Compute last rally text from fresh data
      const freshTeamAId = teamAMap?.id;
      const freshTeamBId = teamBMap?.id;
      const allPlayers = [
        ...(teamAMap?.players ?? []),
        ...(teamBMap?.players ?? []),
      ] as { id: string; name: string }[];
      const sets: any[] = matchData.sets ?? [];
      const currentSet = sets.find((s: any) => !(s.winnerId ?? s.winner_id)) ?? sets[sets.length - 1];
      const rallies: any[] = currentSet?.rallies ?? [];
      const last = [...rallies].sort((a, b) => (b.rally_number ?? b.rallyNumber ?? 0) - (a.rally_number ?? a.rallyNumber ?? 0))[0];
      if (last) {
        const rallyNum = last.rally_number ?? last.rallyNumber ?? 0;
        const pointTeamId = last.point_won_by_id ?? last.pointWonById;
        const reason = (last.lost_reason ?? last.lostReason ?? '').toUpperCase();
        const serverId = last.server_id ?? last.serverId;
        const servTeamId = last.serving_team_id ?? last.servingTeamId;
        const pointWinnerId = last.point_winner_id ?? last.pointWinnerId;
        const serverPlayerName = allPlayers.find(p => p.id === serverId)?.name ?? '';
        const pointWinnerName = allPlayers.find(p => p.id === pointWinnerId)?.name ?? '';
        const servTeamShort = servTeamId === freshTeamAId ? (teamAMap?.short_name ?? teamAMap?.shortName ?? '') : (teamBMap?.short_name ?? teamBMap?.shortName ?? '');
        const pointTeamShort = pointTeamId === freshTeamAId ? (teamAMap?.short_name ?? teamAMap?.shortName ?? '') : (teamBMap?.short_name ?? teamBMap?.shortName ?? '');
        const parts: string[] = [];
        parts.push(`Rally #${rallyNum}`);
        if (serverPlayerName) parts.push(`Serve: ${serverPlayerName} (${servTeamShort})`);
        if (pointTeamShort) parts.push(`Point: ${pointTeamShort}`);
        if (reason) parts.push(`Play: ${reason}${pointWinnerName ? ` — ${pointWinnerName} (${pointTeamShort})` : ''}`);
        const text = parts.join('     ');
        setLastRallyText(prev => prev?.key === rallyNum ? prev : { text, key: rallyNum });
      }

      // Fetch tournament name
      if (matchData.tournamentId || (matchData as any).tournament_id) {
        try {
          const tournament = await getTournament(matchData.tournamentId ?? (matchData as any).tournament_id);
          if (tournament) setTournamentName(tournament.name);
        } catch {
          // Tournament name is optional
        }
      }

      // Fetch club logo from database
      try {
        const logoUrl = await getClubLogoUrl();
        setClubLogoUrl(logoUrl);
      } catch {
        // Club logo is optional
      }

    } catch (err) {
      console.error('[BroadcastOverlay] Error fetching match:', err);
    } finally {
      setLoading(false);
    }
  }, [resolvedBroadcastId]);

  useEffect(() => {
    if (!resolvedBroadcastId) return;
    fetchData();
    pollRef.current = setInterval(fetchData, POLL_INTERVAL);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchData, resolvedBroadcastId]);

  if (loading || !resolvedBroadcastId || !match || !teamA || !teamB) {
    return null;
  }

  // Determine if match is completed
  const isCompleted = match.status === 'match-won';

  // If completed, render the Match Result Overlay
  if (isCompleted) {
    return <MatchResultOverlay
      match={match}
      matchId={match.id ?? (match as any).id}
      teamA={teamA}
      teamB={teamB}
      tournamentName={tournamentName}
      clubLogoUrl={clubLogoUrl}
    />;
  }

  // === LIVE OVERLAY ===
  const sets = match.sets || [];
  const completedSets = sets.filter((s: any) => (s.winnerId ?? s.winner_id));
  const currentSet = sets.find((s: any) => !(s.winnerId ?? s.winner_id)) || sets[sets.length - 1];
  const cs = currentSet as any;
  const currentSetNumber = cs ? (cs.setNumber ?? cs.set_number ?? 1) : 1;

  const teamAId = (match as any).teamAId ?? (match as any).team_a_id;
  const teamBId = (match as any).teamBId ?? (match as any).team_b_id;
  const teamASetsWon = completedSets.filter((s: any) => (s.winnerId ?? s.winner_id) === teamAId).length;
  const teamBSetsWon = completedSets.filter((s: any) => (s.winnerId ?? s.winner_id) === teamBId).length;

  const currentSetScoreA = cs ? (cs.scoreA ?? cs.score_a ?? 0) : 0;
  const currentSetScoreB = cs ? (cs.scoreB ?? cs.score_b ?? 0) : 0;

  const scoreChanged =
    currentSetScoreA !== prevScoreRef.current.teamA ||
    currentSetScoreB !== prevScoreRef.current.teamB;

  if (currentSetScoreA !== prevScoreRef.current.teamA || currentSetScoreB !== prevScoreRef.current.teamB) {
    prevScoreRef.current = { teamA: currentSetScoreA, teamB: currentSetScoreB };
  }

  const matchType = match.matchType ?? (match as any).match_type;
  const rules = MATCH_CONFIG[matchType] ?? null;
  const totalSets = rules?.maxSets ?? 3;

  const setIndicatorsA = Array.from({ length: Math.ceil(totalSets / 2) }, (_, i) => i < teamASetsWon);
  const setIndicatorsB = Array.from({ length: Math.ceil(totalSets / 2) }, (_, i) => i < teamBSetsWon);

  const teamAServing = servingTeam?.id === teamAId;
  const teamBServing = servingTeam?.id === teamBId;

  const isInTimeout = (match.status as string) === 'timeout' ||
    ((match as any).timeouts ?? []).some((t: any) => !t.end_timestamp);
  const activeTimeout = ((match as any).timeouts ?? []).find((t: any) => !t.end_timestamp);
  const timeoutTeam = activeTimeout
    ? (activeTimeout.team_id === teamAId ? teamA : activeTimeout.team_id === teamBId ? teamB : null)
    : null;


  return (
    <>
      <style>{overlayStyles}{`
        @keyframes tb-ticker {
          0%   { transform: translateX(100vw); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
      <div className="fixed bottom-0 left-0 right-0 pointer-events-none" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Commentary ticker — temporarily hidden */}
        {false && lastRallyText && (
          <div style={{
            width: '1200px',
            background: 'rgba(0,0,0,0.75)',
            overflow: 'hidden',
            borderRadius: '8px 8px 0 0',
            padding: '4px 0',
          }}>
            <div
              key={lastRallyText.key}
              style={{
                display: 'inline-block',
                whiteSpace: 'nowrap',
                animation: 'tb-ticker 28s linear 1',
                fontSize: 11,
                fontWeight: 600,
                color: 'rgba(255,255,255,0.8)',
                letterSpacing: '0.03em',
              }}
            >
              {lastRallyText.text}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{lastRallyText.text}
            </div>
          </div>
        )}
        <div className="flex justify-center pb-4" style={{ width: '100%' }}>
        {/* Single horizontal strip */}
        <div
          className="flex items-center gap-6 px-6 py-3 rounded-xl"
          style={{ background: 'rgba(0, 0, 0, 0.90)', minWidth: '1200px' }}
        >
          {/* Club Logo + League Info */}
          <div className="flex items-center gap-3 pr-4 border-r border-white/20">
            <div className="flex-shrink-0">
              {clubLogoUrl ? (
                <img
                  src={clubLogoUrl}
                  alt="Club Logo"
                  className="w-9 h-9 rounded-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <svg viewBox="0 0 40 40" className="w-9 h-9" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="20" cy="20" r="18" fill="#1a1a2e" stroke="#ffd700" strokeWidth="1.5"/>
                  <polygon points="20,6 24,16 35,16 26,22 29,33 20,27 11,33 14,22 5,16 16,16" fill="#ffd700"/>
                </svg>
              )}
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-white font-bold text-lg tracking-wide">Starwood Premier League</span>
              {tournamentName && (
                <span className="text-white/60 text-sm font-medium">{tournamentName}</span>
              )}
            </div>
          </div>

          {/* Team A Section */}
          <div className="flex items-center gap-4">
            {teamA.logoUrl ? (
              <img
                src={teamA.logoUrl}
                alt={teamA.shortName}
                className="w-11 h-11 rounded-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: teamA.color }}
              >
                {teamA.shortName.charAt(0)}
              </div>
            )}

            <span className="font-bold text-xl tracking-wide" style={{ color: teamA.color }}>{teamA.shortName}</span>

            <div
              className={`text-5xl font-black text-white tabular-nums transition-transform duration-200 ${
                scoreChanged && currentSetScoreA > prevScoreRef.current.teamA ? 'scale-110' : ''
              }`}
              style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}
            >
              {currentSetScoreA}
            </div>

            <div className="flex items-center gap-1.5">
              {setIndicatorsA.map((won, i) => (
                <div
                  key={i}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${won ? 'scale-110' : 'opacity-40'}`}
                  style={{ backgroundColor: won ? teamA.color : 'rgba(255,255,255,0.3)' }}
                />
              ))}
            </div>
          </div>

          {/* Center - Set Info */}
          <div className="flex flex-col items-center px-5 border-x border-white/20">
            <span className="text-white text-xs font-bold uppercase tracking-widest">
              SET {currentSetNumber}/{totalSets}
            </span>
            <div className="flex items-center gap-2.5 mt-1">
              <span className="text-2xl font-black" style={{ color: teamA.color }}>{teamASetsWon}</span>
              <span className="text-white/50 text-lg">-</span>
              <span className="text-2xl font-black" style={{ color: teamB.color }}>{teamBSetsWon}</span>
            </div>
          </div>

          {/* Team B Section */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              {setIndicatorsB.map((won, i) => (
                <div
                  key={i}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${won ? 'scale-110' : 'opacity-40'}`}
                  style={{ backgroundColor: won ? teamB.color : 'rgba(255,255,255,0.3)' }}
                />
              ))}
            </div>

            <div
              className={`text-5xl font-black text-white tabular-nums transition-transform duration-200 ${
                scoreChanged && currentSetScoreB > prevScoreRef.current.teamB ? 'scale-110' : ''
              }`}
              style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}
            >
              {currentSetScoreB}
            </div>

            <span className="font-bold text-xl tracking-wide" style={{ color: teamB.color }}>{teamB.shortName}</span>

            {teamB.logoUrl ? (
              <img
                src={teamB.logoUrl}
                alt={teamB.shortName}
                className="w-11 h-11 rounded-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: teamB.color }}
              >
                {teamB.shortName.charAt(0)}
              </div>
            )}
          </div>

          {/* Serving / Timeout Info Section */}
          {(isInTimeout || teamAServing || teamBServing || completedSets.length > 0) && (
            <div className="flex items-center gap-3 pl-4 border-l border-white/20">
              {match.status !== 'set-won' && (isInTimeout && timeoutTeam ? (
                <div className="flex flex-col items-center leading-tight">
                  <span className="text-[10px] font-bold animate-pulse" style={{ color: '#F59E0B' }}>
                    ⏸ TIMEOUT
                  </span>
                  <span className="text-xs font-bold mt-0.5" style={{ color: timeoutTeam.color }}>
                    {timeoutTeam.shortName}
                  </span>
                </div>
              ) : (teamAServing || teamBServing) ? (
                <div className="flex flex-col items-center leading-tight">
                  <span className="text-[10px] font-bold animate-pulse" style={{ color: '#22c55e' }}>
                    SERVING
                  </span>
                  {serverName && (
                    <span className="text-white/80 text-xs font-medium mt-0.5">{serverName}</span>
                  )}
                </div>
              ) : null)}
              {completedSets.length > 0 && (
                <div className="flex flex-col justify-center gap-0.5">
                  {/* Header: team names once */}
                  <div className="flex items-center gap-1 text-[10px] font-bold">
                    <span className="text-white/40 w-5" />
                    <span className="w-10 text-center" style={{ color: teamA.color }}>{teamA.shortName}</span>
                    <span className="w-3 text-center text-white/40">–</span>
                    <span className="w-10 text-center" style={{ color: teamB.color }}>{teamB.shortName}</span>
                  </div>
                  {/* One row per completed set */}
                  {completedSets.map((set: any, idx: number) => {
                    const setNum = set.set_number ?? set.setNumber ?? (idx + 1);
                    const scoreA = set.score_a ?? set.scoreA ?? 0;
                    const scoreB = set.score_b ?? set.scoreB ?? 0;
                    const winnerId = set.winner_id ?? set.winnerId;
                    return (
                      <div key={idx} className="flex items-center gap-1 text-[11px] font-bold">
                        <span className="text-white/50 w-5">S{setNum}</span>
                        <span className={`w-10 text-center tabular-nums ${winnerId === teamAId ? 'text-white' : 'text-white/40'}`}>{scoreA}</span>
                        <span className="w-3 text-center text-white/30">–</span>
                        <span className={`w-10 text-center tabular-nums ${winnerId === teamBId ? 'text-white' : 'text-white/40'}`}>{scoreB}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </>
  );
}

// === MATCH RESULT OVERLAY COMPONENT ===
function MatchResultOverlay({ match, matchId, teamA, teamB, tournamentName, clubLogoUrl }: {
  match: MatchData;
  matchId: string;
  teamA: TeamInfo;
  teamB: TeamInfo;
  tournamentName: string;
  clubLogoUrl: string | null;
}) {
  const [pom, setPom] = useState<{ playerName: string; teamId: string } | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Fetch player of the match
    getPlayerOfMatch(matchId)
      .then(winners => { if (winners[0]) setPom({ playerName: winners[0].playerName, teamId: winners[0].teamId }); })
      .catch(() => {});

    // Fireworks container sized to the actual bar height, kept in sync via ResizeObserver
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.bottom = '0';
    container.style.left = '0';
    container.style.width = '100%';
    container.style.pointerEvents = 'none';
    container.style.zIndex = '9999';
    container.style.overflow = 'hidden';
    document.body.appendChild(container);

    const syncHeight = () => {
      const h = barRef.current ? barRef.current.offsetHeight : 90;
      container.style.height = h + 'px';
    };
    syncHeight();
    const ro = new ResizeObserver(syncHeight);
    if (barRef.current) ro.observe(barRef.current);

    let fw: any;
    let cancelled = false;

    (async () => {
      const { Fireworks } = await import('fireworks-js');
      if (cancelled) return;
      fw = new Fireworks(container, ({
        rocketsPoint: { min: 20, max: 80 },
        speed: 2,
        acceleration: 1.05,
        friction: 0.97,
        gravity: 1.5,
        particles: 80,
        trace: 2,
        explosion: 6,
        intensity: 25,
        flickering: 60,
        lineStyle: 'round',
        hue: { min: 0, max: 360 },
        delay: { min: 40, max: 80 },
        boundaries: { x: 50, y: 50, width: container.clientWidth - 100, height: container.clientHeight - 50 },
        sound: { enabled: false },
        lineWidth: { explosion: { min: 1, max: 3 }, trace: { min: 0.1, max: 1 } },
        brightness: { min: 50, max: 80 },
        decay: { min: 0.015, max: 0.03 },
        mouse: { click: false, move: false, max: 1 },
      }) as any);
      fw.start();
    })();

    return () => {
      cancelled = true;
      if (fw) fw.stop();
      ro.disconnect();
      container.remove();
    };
  }, [matchId]);
  const winnerId = match.winnerId ?? (match as any).winner_id;
  const winner = winnerId === teamA.id ? teamA : winnerId === teamB.id ? teamB : null;

  const winnerSetsWon = winnerId === teamA.id
    ? match.sets?.filter((s: any) => (s.winner_id ?? s.winnerId) === teamA.id).length ?? 0
    : match.sets?.filter((s: any) => (s.winner_id ?? s.winnerId) === teamB.id).length ?? 0;

  const loserSetsWon = winnerId === teamA.id
    ? match.sets?.filter((s: any) => (s.winner_id ?? s.winnerId) === teamB.id).length ?? 0
    : match.sets?.filter((s: any) => (s.winner_id ?? s.winnerId) === teamA.id).length ?? 0;

  // Completed sets for display
  const completedSets = (match.sets ?? []).filter((s: any) => s.winner_id || s.winnerId);

  return (
    <>
      <style>{overlayStyles}</style>
      <div className="fixed bottom-0 left-0 right-0 flex justify-center pb-4 pointer-events-none">
        {/* Single horizontal result strip */}
        <div
          ref={barRef}
          className="flex items-center justify-center gap-6 px-6 py-3 rounded-xl"
          style={{ background: 'rgba(0, 0, 0, 0.90)' }}
        >
          {/* Club Logo + League Info */}
          <div className="flex items-center gap-3 pr-4 border-r border-white/20">
            <div className="flex-shrink-0">
              {clubLogoUrl ? (
                <img
                  src={clubLogoUrl}
                  alt="Club Logo"
                  className="w-9 h-9 rounded-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <svg viewBox="0 0 40 40" className="w-9 h-9" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="20" cy="20" r="18" fill="#1a1a2e" stroke="#ffd700" strokeWidth="1.5"/>
                  <polygon points="20,6 24,16 35,16 26,22 29,33 20,27 11,33 14,22 5,16 16,16" fill="#ffd700"/>
                </svg>
              )}
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-white font-bold text-lg tracking-wide">Starwood Premier League</span>
              {tournamentName && (
                <span className="text-white/60 text-sm font-medium">{tournamentName}</span>
              )}
            </div>
          </div>

          {/* Winner Section */}
          <div className="flex items-center gap-4">
            <span className="text-3xl">🏆</span>
            {winner && (
              <>
                {winner.logoUrl ? (
                  <img
                    src={winner.logoUrl}
                    alt={winner.shortName}
                    className="w-11 h-11 rounded-full object-cover border-2 border-white/30"
                  />
                ) : (
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-xl border-2 border-white/30"
                    style={{ backgroundColor: winner.color }}
                  >
                    {winner.shortName.charAt(0)}
                  </div>
                )}
                <div className="flex flex-col leading-tight">
                  <span className="font-bold text-xl" style={{ color: winner.color }}>{winner.name}</span>
                  <span className="font-semibold text-base">
                    <span style={{ color: winner.color }}>{winner.shortName}</span>
                    <span className="text-white/80"> won {winnerSetsWon} – {loserSetsWon}</span>
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Separator */}
          <div className="w-px h-10 bg-white/20"></div>

          {/* Set Summary */}
          {completedSets.length > 0 && (
            <div className="flex flex-col justify-center gap-0.5">
              {/* Header: team names once */}
              <div className="flex items-center gap-1 text-[10px] font-bold">
                <span className="text-white/40 w-5" />
                <span className="w-10 text-center" style={{ color: teamA.color }}>{teamA.shortName}</span>
                <span className="w-3 text-center text-white/40">–</span>
                <span className="w-10 text-center" style={{ color: teamB.color }}>{teamB.shortName}</span>
              </div>
              {/* One row per set */}
              {completedSets.map((set: any, idx) => {
                const setNum = set.set_number ?? set.setNumber ?? (idx + 1);
                const scoreA = set.score_a ?? set.scoreA ?? 0;
                const scoreB = set.score_b ?? set.scoreB ?? 0;
                const winnerId = set.winner_id ?? set.winnerId;
                return (
                  <div key={idx} className="flex items-center gap-1 text-[11px] font-bold">
                    <span className="text-white/50 w-5">S{setNum}</span>
                    <span className={`w-10 text-center tabular-nums ${winnerId === teamA.id ? 'text-white' : 'text-white/40'}`}>{scoreA}</span>
                    <span className="w-3 text-center text-white/30">–</span>
                    <span className={`w-10 text-center tabular-nums ${winnerId === teamB.id ? 'text-white' : 'text-white/40'}`}>{scoreB}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Player of the Match */}
          {pom && (() => {
            const pomTeam = pom.teamId === teamA.id ? teamA : pom.teamId === teamB.id ? teamB : null;
            return pomTeam ? (
              <>
                <div className="w-px h-10 bg-white/20" />
                <div className="flex items-center gap-2">
                  <span className="text-xl leading-none">⭐</span>
                  <div className="flex flex-col leading-tight">
                    <span className="text-white/60 text-[9px] font-bold uppercase tracking-widest whitespace-nowrap">Player of Match</span>
                    <span className="font-bold text-base whitespace-nowrap">
                      <span className="text-white">{pom.playerName}</span>
                      {' '}
                      <span style={{ color: pomTeam.color }}>({pomTeam.shortName})</span>
                    </span>
                  </div>
                </div>
              </>
            ) : null;
          })()}
        </div>
      </div>
    </>
  );
}
